import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Sheet,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Button,
  CircularProgress,
  Alert,
  Box,
  Select,
  Option,
} from '@mui/joy';
import PhoneIcon from '@mui/icons-material/Phone';
import SettingsEthernetIcon from '@mui/icons-material/SettingsEthernet';
import { listPhoneNumbers, listDeployments } from '../../api/agent';
import { fetchRegistrations } from '../../api/registrations';

function normalizeHandler(handler) {
  const h = handler != null ? String(handler).trim() : '';
  return h ? h : null;
}

function isUsableRegistrationForInbound(reg) {
  // EXPERIMENTAL (commcon-sipgate): we overload the outbound-registration
  // metaphor to drive an inbound hardwired SIP REGISTER on voiceblender. There
  // is no working outbound registration, so such an endpoint never reaches
  // state === 'registered'. Relax the gate to allow listening on any
  // registration that isn't explicitly disabled, instead of requiring the
  // 'active'/'registered' pair. Revert this for non-experimental builds.
  const status = String(reg?.status || '').toLowerCase();
  return status !== 'disabled';
}

export default function TelephonyResourceSelector({
  open,
  onClose,
  onSelect,
  loading = false,
  agentHandler,
}) {
  const [mode, setMode] = useState('phone'); // 'phone' | 'registration'
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode('phone');
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingData(true);
      setError(null);
      try {
        const handler = normalizeHandler(agentHandler);
        const [deployData, numbers, regs] = await Promise.all([
          listDeployments(),
          listPhoneNumbers(false, handler || undefined),
          fetchRegistrations(),
        ]);
        if (cancelled) return;
        setDeployments(Array.isArray(deployData?.items) ? deployData.items : []);
        setPhoneNumbers(Array.isArray(numbers) ? numbers : []);
        const allRegs = Array.isArray(regs) ? regs : [];
        const filteredRegs =
          handler
            ? allRegs.filter((r) => !r.handler || String(r.handler).trim() === handler)
            : allRegs;
        setRegistrations(filteredRegs);
      } catch (err) {
        if (!cancelled) {
          setError(`Failed to load telephony resources: ${err?.message || String(err)}`);
          setDeployments([]);
          setPhoneNumbers([]);
          setRegistrations([]);
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, agentHandler]);

  const inUseNumbers = useMemo(() => {
    const s = new Set();
    (deployments || []).forEach((d) => {
      if (d?.kind === 'phone' && d?.phoneNumber) s.add(d.phoneNumber);
    });
    return s;
  }, [deployments]);

  const inUseRegistrations = useMemo(() => {
    const s = new Set();
    (deployments || []).forEach((d) => {
      if (d?.kind === 'registration' && d?.registrationId) s.add(d.registrationId);
    });
    return s;
  }, [deployments]);

  const availablePhoneNumbers = useMemo(
    () => (phoneNumbers || []).filter((p) => p?.number && !inUseNumbers.has(p.number)),
    [phoneNumbers, inUseNumbers],
  );

  const availableRegistrations = useMemo(
    () =>
      (registrations || []).filter(
        (r) => r?.id && !inUseRegistrations.has(r.id) && isUsableRegistrationForInbound(r),
      ),
    [registrations, inUseRegistrations],
  );

  const handlePick = (payload) => {
    onSelect?.(payload);
    onClose?.();
  };

  const listEmpty =
    mode === 'phone'
      ? availablePhoneNumbers.length === 0
      : availableRegistrations.length === 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog aria-labelledby="telephony-resource-selector" size="md" sx={{ minWidth: 440 }}>
        <ModalClose />
        <Typography id="telephony-resource-selector" component="h2" level="h4" fontWeight="lg" mb={1}>
          Select telephony resource
        </Typography>
        <Typography level="body-sm" mb={2}>
          Choose a phone number or SIP registration endpoint for your agent to listen on.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <Typography level="body-sm" textColor="neutral.600">
            Resource type
          </Typography>
          <Select
            size="sm"
            value={mode}
            onChange={(_, v) => setMode(v || 'phone')}
            sx={{ minWidth: 220 }}
          >
            <Option value="phone">Telephone Number</Option>
            <Option value="registration">SIP Registration</Option>
          </Select>
        </Box>

        {loadingData ? (
          <Sheet sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Sheet>
        ) : error ? (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : listEmpty ? (
          <Alert color="warning" sx={{ mb: 2 }}>
            {mode === 'phone'
              ? 'No available phone numbers (all are in use or none allocated).'
              : 'No available SIP registrations (all are in use or none configured).'}
          </Alert>
        ) : (
          <List sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {mode === 'phone' &&
              availablePhoneNumbers.map((p) => (
                <ListItem key={p.number}>
                  <ListItemButton
                    onClick={() => handlePick({ kind: 'phone', number: p.number })}
                    disabled={loading}
                  >
                    <ListItemDecorator>
                      <PhoneIcon />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="title-sm">{p.number}</Typography>
                      <Typography level="body-xs" textColor="neutral.500">
                        {p.handler} • {p.outbound ? 'Outbound enabled' : 'Inbound only'}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}

            {mode === 'registration' &&
              availableRegistrations.map((r) => (
                <ListItem key={r.id}>
                  <ListItemButton
                    onClick={() => handlePick({ kind: 'registration', id: r.id })}
                    disabled={loading}
                  >
                    <ListItemDecorator>
                      <SettingsEthernetIcon />
                    </ListItemDecorator>
                    <ListItemContent>
                      <Typography level="title-sm">{(r.name || r.registrar || 'Registration').trim?.() || r.id}</Typography>
                      <Typography level="body-xs" textColor="neutral.500">
                        {r.registrar || '–'}
                        {r.handler ? ` • ${r.handler}` : ''}
                      </Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        )}

        <Sheet sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </Sheet>
      </ModalDialog>
    </Modal>
  );
}

